/** Convenience hook bundling auth store + login/logout actions.
 *
 * Each call to `useAuth()` returns a fresh object — that's fine for
 * direct consumers, but DON'T pass the return through `useMemo` deps or
 * pass the whole object as a prop. Pull what you need (user, hasPerm,
 * etc.) into local consts.
 *
 * Selectors below subscribe individually so a change to e.g. user
 * doesn't re-render components that only use logout. */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import * as authApi from '@/api/auth'

export function useAuth() {
  const navigate = useNavigate()
  // Subscribe to fields independently so unrelated changes don't re-render
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const setSession = useAuthStore((s) => s.setSession)
  const storeLogout = useAuthStore((s) => s.logout)
  const hasPerm = useAuthStore((s) => s.hasPerm)
  const hasRole = useAuthStore((s) => s.hasRole)

  const requestOtp = useCallback(
    async (email: string) =>
      authApi.requestOtp({
        email,
        device_fingerprint: authApi.deviceFingerprint(),
      }),
    [],
  )

  const verifyOtp = useCallback(
    async (email: string, code: string) => {
      const r = await authApi.verifyOtp({
        email,
        code,
        device_fingerprint: authApi.deviceFingerprint(),
      })
      setSession(r.token, r.user)
      return r
    },
    [setSession],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // best-effort — server may already have invalidated session
    }
    storeLogout()
    navigate('/login', { replace: true })
  }, [navigate, storeLogout])

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    requestOtp,
    verifyOtp,
    logout,
    hasPerm,
    hasRole,
  }
}
